import React from "react";
import useShow from "../hooks/useShow";
import { get_opporunity } from "../../../service/api/opportunity";
import { Link, useParams } from "react-router-dom";
import { setTitle } from "../../../store/slices/navbar";
import { useAppDispatch, useAppSelector } from "../../../store";
import { formatDate } from "../../../service/convert/date";
import { IconFileText } from "@tabler/icons-react";
import { Loader } from "../../../components/animation/Loader";

const OpportunityDetails = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { opportunity, loading } = useAppSelector(
    ({ opportunity }) => opportunity,
  );

  const { image } = useShow();

  const onLoad = React.useCallback(() => {
    dispatch(setTitle("Opportunity"));
    if (id) get_opporunity(dispatch, id);
  }, [dispatch, id]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="overflow-y-auto">
      <div className="flex flex-col lg:grid lg:grid-cols-3 items-start gap-4 lg:gap-6">
        {/* Image Section */}
        <div className="w-full lg:col-span-2 bg-white shadow-sm rounded-xl overflow-hidden">
          <div
            style={{
              backgroundImage: `url(${image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            className="relative h-[220px] sm:h-[300px] lg:h-[400px] w-full bg-gray-100"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-dark/80 to-transparent" />
            <span
              className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-xs sm:text-sm font-medium rounded-full px-2.5 py-1 ${
                opportunity?.is_active
                  ? "text-primary-800 bg-customgreen"
                  : "text-red-800 bg-red-200/50"
              }`}
            >
              {opportunity?.is_active ? "Active" : "Expired"}
            </span>
          </div>
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 leading-snug">
              {opportunity?.opportunity_name}
            </h1>

            <hr className="w-6/12 sm:w-3/12" />

            <div
              dangerouslySetInnerHTML={{
                __html: opportunity?.opportunity_description || "",
              }}
              className="max-w-none text-sm sm:text-base text-gray-700 
                         [&>h1]:text-xl sm:[&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mb-4 
                         [&>h2]:text-lg sm:[&>h2]:text-xl [&>h2]:font-semibold [&>h2]:mb-3
                         [&>p]:mb-4 [&>ul]:list-disc [&>ul]:ml-4 [&>ol]:list-decimal [&>ol]:ml-4
                         [&>img]:max-w-full [&>img]:h-auto [&>img]:rounded-lg"
            />
          </div>
        </div>

        {/* Content Section */}
        <div className="w-full p-4 sm:p-6 flex flex-col bg-white shadow-sm rounded-xl">
          <p className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm sm:text-md font-medium text-gray-500 mb-4 sm:mb-6 gap-1">
            <span>Opportunity files</span>
            {opportunity?.expire_date && (
              <span className="font-semibold text-gray-700">
                {formatDate(opportunity.expire_date)}
              </span>
            )}
          </p>

          <div className="space-y-4">
            {!opportunity?.opportunity_files ? (
              <p className="text-sm text-gray-500">No files uploaded.</p>
            ) : (
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50/10 sm:border-0 sm:bg-transparent sm:p-0">
                <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div className="flex-shrink-0 bg-primary-50/20 p-2 rounded-md text-primary-600">
                    <IconFileText size={20} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-lg font-medium text-gray-900 truncate">
                      Opportunity file
                    </h3>
                  </div>
                </div>

                <Link
                  to={opportunity.opportunity_files}
                  target="_blank"
                  className="flex-shrink-0 bg-primary-600 hover:bg-primary-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  View
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpportunityDetails;
